import React, { useMemo } from 'react';

import styled from '@emotion/styled';

interface LabelProps {
  textColor?: string;
  fontSize?: string;
  fontWeight?: string;
  lineHeight?: string;
  marginBottom?: string;
  marginTop?: string;
}
interface CustomHorizontalBarChatProps {
  data: Record<string, any>[];
  leftLabelKey: string;
  dataKey: string;
  colorKey?: string;
  lineThickness?: number;
  roundness?: number;
  marginLeft?: string;
  marginRight?: string;
  leftLabelWidth?: string;
  rightLabelWidth?: string;
  label?: LabelProps;
  description: string;
}
const CustomHorizontalBarChat = ({
  data,
  leftLabelKey,
  dataKey,
  lineThickness,
  colorKey,
  roundness,
  marginLeft,
  marginRight,
  leftLabelWidth,
  rightLabelWidth,
  label,
  description,
}: CustomHorizontalBarChatProps) => {
  const normalizedData = useMemo(() => {
    const values = data.map((item) => Number(item[dataKey]) || 0);
    const maxValue = Math.max(...values, 0);

    return values.map((value) =>
      maxValue > 0 ? ((value / maxValue) * 100).toFixed(2) : '0.00',
    );
  }, [data, dataKey]);

  return (
    <Container>
      <Label {...label}>{description}</Label>
      {normalizedData.map((value, index) => (
        <BarArea
          key={`bar-area-${index}`}
          leftLabelWidth={leftLabelWidth}
          rightLabelWidth={rightLabelWidth}
        >
          <XLabel key={`x-label-${index}`}>{data[index][leftLabelKey]}</XLabel>
          <ChartLine
            key={`chart-line-${index}`}
            lineThickness={lineThickness}
            roundness={roundness}
            marginLeft={marginLeft}
            marginRight={marginRight}
          >
            <IndicationLine
              lineThickness={lineThickness}
              roundness={roundness}
              style={{ width: `${value}%` }}
              color={colorKey && data[index][colorKey]}
            />
          </ChartLine>
          <ValueCount>{data[index][dataKey]}</ValueCount>
        </BarArea>
      ))}
    </Container>
  );
};

export default CustomHorizontalBarChat;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const Label = styled.div<LabelProps>`
  font-weight: ${({ fontWeight }) => fontWeight ?? '400'};
  font-size: ${({ fontSize }) => fontSize ?? '12px'};
  line-height: ${({ lineHeight }) => lineHeight ?? '100%'};
  color: ${({ textColor }) => textColor ?? '#6b7280'};
  margin-bottom: ${({ marginBottom }) => marginBottom ?? '0'};
  margin-top: ${({ marginTop }) => marginTop ?? '0'};
`;

const BarArea = styled.div<{
  leftLabelWidth?: string;
  rightLabelWidth?: string;
}>`
  width: 100%;
  height: fit-content;
  display: grid;
  align-items: center;
  justify-content: center;
  grid-template-columns:
    ${({ leftLabelWidth }) => leftLabelWidth ?? '120px'} minmax(0, 1fr)
    ${({ rightLabelWidth }) => rightLabelWidth ?? '48px'};
  gap: 30px;
`;

const XLabel = styled.div`
  font-size: 14px;
  line-height: 14px;
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.grayTextColor};
  color: #2f3644;
`;

const ChartLine = styled.div<{
  lineThickness?: number;
  roundness?: number;
  marginLeft?: string;
  marginRight?: string;
}>`
  width: auto;
  min-width: 0;
  background-color: #e0e0e0;
  height: ${({ lineThickness }) => lineThickness ?? 8}px;
  border-radius: ${({ roundness }) => roundness ?? 4}px;
  margin-left: ${({ marginLeft }) => marginLeft ?? '0'};
  margin-right: ${({ marginRight }) => marginRight ?? '0'};
  overflow: hidden;

  display: grid;
  & > * {
    grid-column: 1;
    grid-row: 1;
  }
`;

const IndicationLine = styled.div<{
  lineThickness?: number;
  roundness?: number;
  color?: string;
}>`
  width: 100%;
  height: 100%;
  transform-origin: left center;
  animation: grow-indication-line 1.5s ease-out;
  background-color: ${({ color, theme }) =>
    color ?? theme.palette.blueColor ?? '#5B91F5'};

  height: ${({ lineThickness }) => lineThickness ?? 8}px;
  border-radius: ${({ roundness }) => roundness ?? 4}px;

  @keyframes grow-indication-line {
    from {
      transform: scaleX(0);
    }
    to {
      transform: scaleX(1);
    }
  }
`;

const ValueCount = styled.div`
  font-size: 12px;
  font-weight: 600;
  width: fit-content;
  text-align: right;
  color: #2f3644;
`;
